import { FetchError } from 'ofetch'
import { defineStore } from 'pinia'
import type { OptionInterface } from '~/components/base/controls/use-input'
import { toAutocomplete } from '~/utils/autocomplete'
import type {
  Company,
  CompanyCreate,
  CompanyLite,
  CompanyUpdate,
} from '~/models/companies'

const API_ENDPOINT = '/companies'

interface CompanyState {
  company: Company | null
  companyProps: {
    id: number | null
  }
}

export const useCompanies = defineStore('companies', () => {
  const baseURL = useRuntimeConfig().public.gatewayApi

  const state = reactive<CompanyState>({
    company: null,
    companyProps: { id: null },
  })

  async function getCompaniesAutocomplete(
    query?: string
  ): Promise<OptionInterface[]> {
    const { data } = await useAuthFetch<{
      data: Pick<CompanyLite, 'id' | 'name'>[]
    }>(`${API_ENDPOINT}/autocomplete`, {
      baseURL,
      params: {
        query,
      },
    })

    return data.map(toAutocomplete)
  }

  const companiesList = useInfinityList<Company>('', baseURL)

  const initCompaniesList = (eventId: string) => {
    const endpoint = `/${eventId}/companies`
    if (companiesList.state.endpoint === endpoint) {
      return
    }
    companiesList.reset()
    companiesList.state.endpoint = `/${eventId}/companies`
  }

  async function getCompaniesOptions(
    query: string,
    addNewValue = true
  ): Promise<OptionInterface[]> {
    let options: OptionInterface[] = await getCompaniesAutocomplete(query)

    if (
      query &&
      !options.find(
        ({ label }) => label.toLocaleLowerCase() === query.toLocaleLowerCase()
      ) &&
      addNewValue
    ) {
      options = [{ value: query, label: query }, ...options]
    }

    return options
  }

  async function fetchCompany(id: number): Promise<void> {
    if (state.companyProps.id === id) {
      return
    }

    state.company = null

    state.companyProps = { id }

    try {
      const { data } = await useAuthFetch<{ data: Company }>(
        `${API_ENDPOINT}/${id}`,
        {
          baseURL,
        }
      )

      if (state.companyProps.id === data.id) {
        state.company = data
      }
    } catch (error: any) {
      if ((error as FetchError).status === 404) {
        throw useCreateScreenError({ message: 'company not found' })
      } else {
        throw error
      }
    }
  }

  async function getCompany(id: number) {
    try {
      const { data } = await useAuthFetch<{ data: Company }>(
        `${API_ENDPOINT}/${id}`,
        {
          baseURL,
        }
      )

      return data
    } catch (error) {
      if ((error as FetchError).status !== 404) {
        throw error
      } else {
        return null
      }
    }
  }

  function resetCompany() {
    state.companyProps = { id: null }
    state.company = null
  }

  async function createCompany(companyData: CompanyCreate): Promise<void> {
    const { data } = await useAuthFetch<{
      data: Company
    }>(API_ENDPOINT, {
      baseURL,
      method: 'POST',
      body: companyData,
    })

    state.companyProps = { id: data.id }
    state.company = data
  }

  async function updateCompany(
    id: number,
    companyData: CompanyUpdate
  ): Promise<void> {
    const { data } = await useAuthFetch<{
      data: Company
    }>(`${API_ENDPOINT}/${id}`, {
      baseURL,
      method: 'PUT',
      body: companyData,
    })

    state.companyProps = { id: data.id }
    state.company = data
  }

  return {
    company: toRef(state, 'company'),
    companyProps: toRef(state, 'companyProps'), // not used externally, but return to save state server-client
    fetchCompany,
    getCompany,
    getCompaniesAutocomplete,
    getCompaniesOptions,
    createCompany,
    updateCompany,
    resetCompany,
    initCompaniesList,
    list: companiesList.state,
    getPage: companiesList.getPage,
    nextPage: companiesList.nextPage,
    allRecordsLoaded: companiesList.allRecordsLoaded,
  }
})
